import { Component, OnInit, ElementRef, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';

interface Particle {
  x: number;
  y: number;
  size: number;
  speed: number;
  drift: number;
  rot?: number;
  rotSpeed?: number;
  isEmoji?: boolean;
}

@Component({
  selector: 'app-festive-banner',
  templateUrl: './festive-banner.component.html',
  styleUrls: ['./festive-banner.component.scss']
})
export class FestiveBannerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('emojiSnow', { static: true })
  canvasRef!: ElementRef<HTMLCanvasElement>;
  
  private ctx!: CanvasRenderingContext2D;
  private particles: Particle[] = [];
  private animationId!: number;
  private resizeHandler!: () => void;
  
  readonly SNOW_COUNT = 250;
  readonly EMOJI_COUNT = 250;
  readonly DOT_COUNT = 150;

  constructor() { }

  ngOnInit(): void {
  }
  ngAfterViewInit(): void {
    this.initCanvas();
    this.createParticles();
    this.animate();

    this.resizeHandler = () => this.resize();
    window.addEventListener('resize', this.resizeHandler);
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.animationId);
    window.removeEventListener('resize', this.resizeHandler);
  }

  private initCanvas(): void {
    const canvas = this.canvasRef.nativeElement;
    this.ctx = canvas.getContext('2d')!;
    this.resize();
  }

  private resize(): void {
    const canvas = this.canvasRef.nativeElement;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  }

  private createParticles(): void {
    const canvas = this.canvasRef.nativeElement;

    // Emoji snowflakes
    for (let i = 0; i < this.EMOJI_COUNT; i++) {
      this.particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 10 + 8,
        speed: Math.random() * 1.5 + 0.5,
        drift: Math.random() * 0.6 - 0.3,
        rot: Math.random() * Math.PI * 2,
        rotSpeed: Math.random() * 0.01 - 0.005,
        isEmoji: true
      });
    }

    // Small dots
    for (let i = 0; i < this.DOT_COUNT; i++) {
      this.particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 2 + 1,
        speed: Math.random() * 1 + 0.2,
        drift: Math.random() * 0.5 - 0.25,
        isEmoji: false
      });
    }
  }

  private animate = (): void => {
    const canvas = this.canvasRef.nativeElement;
    const { width, height } = canvas;

    this.ctx.clearRect(0, 0, width, height);

    this.particles.forEach(p => {
  this.ctx.save();
  this.ctx.translate(p.x, p.y);

  if (p.isEmoji) {
    // Emoji snowflake
    this.ctx.rotate(p.rot!);
    this.ctx.shadowBlur = Math.random() * 4;
    this.ctx.shadowColor = 'white';
    this.ctx.font = `${p.size}px Arial`;
    this.ctx.fillText('❄️', 0, 0);
  } else {
    // Small dot
    this.ctx.beginPath();
    this.ctx.fillStyle = 'white';
    
    // 🔹 Smooth blur proportional to size
    const minBlur = 1;
    const maxBlur = 5;
    this.ctx.shadowBlur = minBlur + (p.size / 8) * (maxBlur - minBlur); // small size → small blur, bigger dot → bigger blur
    this.ctx.shadowColor = 'white';
    
    this.ctx.arc(0, 0, p.size, 0, Math.PI * 2);
    this.ctx.fill();
  }

  // Inside animate loop
if (!p.isEmoji) {
  this.ctx.save();
  this.ctx.translate(p.x, p.y);

  this.ctx.beginPath();
  this.ctx.fillStyle = 'white';

  // 🔹 Fixed blur and spread
  const blur = 16;       // glow
  const spread = 0;     // multiple layers
  this.ctx.shadowBlur = blur;
  this.ctx.shadowColor = 'white';

  // 🔹 Spread effect: 19 layers to create big soft dot
  for (let s = 0; s < spread; s++) {
    this.ctx.arc(0, 0, 1 + s, 0, Math.PI * 2); // dot size 1px + spread
    this.ctx.fill();
  }

  this.ctx.restore();
}

  this.ctx.restore();

  // Update positions
  p.y += p.speed;
  p.x += p.drift;
  if (p.isEmoji) p.rot! += p.rotSpeed!;

  // Reset if out of canvas
  const canvas = this.canvasRef.nativeElement;
  const { width, height } = canvas;

  if (p.y > height + 50) {
    p.y = -50;
    p.x = Math.random() * width;
  }
  if (p.x > width || p.x < 0) {
    p.x = Math.random() * width;
  }
});

    this.animationId = requestAnimationFrame(this.animate);
  };
}
